import React from 'react';
import i18n from 'util/i18n';
import { HISTORY_RESIDENCE } from 'constants/sections';
import ConnectedAlternateAddress from 'components/Form/Location/AlternateAddress';
import { LAST_THREE_YEARS } from 'constants/dateLimits';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { HistoryResidenceItemModel } from 'forms/sections/history/residence/types';
import {
  CommonOnUpdate, CommonOnError, CommonRequired, CommonAddressBooks
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import classnames from 'classnames';
import { dateWithinMonthYearSafeRange, isValid } from 'helpers/date';
import { DateTime } from 'luxon';
import { CheckboxModel, DateModel } from 'forms/shared/types';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Show from 'components/Form/Show';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Name from 'components/Form/Name';
import DateControl from 'components/Form/DateControl';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Telephone from 'components/Form/Telephone';
import NotApplicable from 'components/Form/NotApplicable';
import Email from 'components/Form/Email';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import PVQLocation from 'components/Form/PVQLocation';
import Branch from 'components/Form/Branch';
import Dropdown from 'components/Form/Dropdown';
import { temporaryLivingSituationOptions } from 'constants/enums/residenceOptions';
import Textarea from 'components/Form/Textarea';
import { isPVQForm } from 'helpers/form';

const eqipResidenceVerifierRequired = DateTime.fromJSDate(new Date(LAST_THREE_YEARS.year, LAST_THREE_YEARS.month - 1, 1, 0));

// We need to determine how far back 3 years ago was
const withinThreeYears = (from?: DateModel, to?: DateModel, present?: boolean) => present
  || dateWithinMonthYearSafeRange(from, eqipResidenceVerifierRequired)
  || dateWithinMonthYearSafeRange(to, eqipResidenceVerifierRequired);

interface Props
  extends ValidationElementProps,
  AccordionItemProps,
  Partial<HistoryResidenceItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonRequired
  addressBooks?: CommonAddressBooks
  required?: CommonRequired

  // TODO: Remove
  dispatch?: Dispatch

  requireHistoryResidenceReferenceInfo?: boolean
  requireHistoryResidenceRole?: boolean
  requireHistoryResidenceReferenceName?: boolean
}

/**
 * Residence item in a collection
 *
 * This was broken apart so it could manage minor local state in determining
 * when particular portions of this should be rendered.
 */
export default class ResidenceItem extends ValidationElement<Props> {
  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  componentWillUnmount() {
    const dates = this.props.Dates || {};
    const { from, to } = dates;
    // Only clear out the reference if the dates are outside 3 years and date is fully valid.
    const zeroReference = !withinThreeYears(from, to, dates.present) && isValid(from) && (isValid(to) || dates.present);
    this.update({
      Dates: dates,
      ReferenceName: zeroReference ? {} : this.props.ReferenceName,
      ReferenceName2013: zeroReference ? {} : this.props.ReferenceName2013,
      ReferenceLastContact: zeroReference ? {} : this.props.ReferenceLastContact,
      ReferenceRelationshipComments: zeroReference ? {} : this.props.ReferenceRelationshipComments,
      ReferenceRelationship: zeroReference ? {} : this.props.ReferenceRelationship,
      ReferenceRelationshipOther: zeroReference ? {} : this.props.ReferenceRelationshipOther,
      ReferencePhoneEvening: zeroReference ? {} : this.props.ReferencePhoneEvening,
      ReferencePhoneDay: zeroReference ? {} : this.props.ReferencePhoneDay,
      ReferencePhoneMobile: zeroReference ? {} : this.props.ReferencePhoneMobile,
      ReferenceEmailNotApplicable: zeroReference ? { applicable: true } : this.props.ReferenceEmailNotApplicable,
      ReferenceEmail: zeroReference ? {} : this.props.ReferenceEmail,
      ReferenceAddress: zeroReference ? {} : this.props.ReferenceAddress,
      ReferenceComments: zeroReference ? {} : this.props.ReferenceComments,
      ReferenceAlternateAddressPhysicalComments: zeroReference ? {} : this.props.ReferenceAlternateAddressPhysicalComments,
      ReferenceAlternateAddressAnswerComments: zeroReference ? {} : this.props.ReferenceAlternateAddressAnswerComments,
      ReferenceAlternateAddressComments: zeroReference ? {} : this.props.ReferenceAlternateAddressComments,
    });
  }

  /**
   * Handle any updates and bubble them up.
   */
  update = (queue: Partial<HistoryResidenceItemModel>) => {
    this.props.onUpdate({
      Address: this.props.Address,
      AlternateAddress: this.props.AlternateAddress,
      Comments: this.props.Comments,
      AddressPhysicalComments: this.props.AddressPhysicalComments,
      AddressComments: this.props.AddressComments,
      AddressAnswerComments: this.props.AddressAnswerComments,
      ReferenceComments: this.props.ReferenceComments,
      ReferenceAlternateAddressPhysicalComments: this.props.ReferenceAlternateAddressPhysicalComments,
      ReferenceAlternateAddressAnswerComments: this.props.ReferenceAlternateAddressAnswerComments,
      ReferenceAlternateAddressComments: this.props.ReferenceAlternateAddressComments,
      Dates: this.props.Dates,
      IsTemporaryAddress: this.props.IsTemporaryAddress,
      ReferenceAddress: this.props.ReferenceAddress,
      ReferenceAlternateAddress: this.props.ReferenceAlternateAddress,
      ReferenceEmail: this.props.ReferenceEmail,
      ReferenceEmailNotApplicable: this.props.ReferenceEmailNotApplicable,
      ReferenceLastContact: this.props.ReferenceLastContact,
      ReferenceName: this.props.ReferenceName,
      ReferenceName2013: this.props.ReferenceName2013,
      ReferencePhoneDay: this.props.ReferencePhoneDay,
      ReferencePhoneEvening: this.props.ReferencePhoneEvening,
      ReferencePhoneMobile: this.props.ReferencePhoneMobile,
      ReferenceRelationship: this.props.ReferenceRelationship,
      ReferenceRelationshipComments: this.props.ReferenceRelationshipComments,
      ReferenceRelationshipOther: this.props.ReferenceRelationshipOther,
      Role: this.props.Role,
      RoleOther: this.props.RoleOther,
      TemporaryAddressPurpose: this.props.TemporaryAddressPurpose,
      TemporaryAddressPurposeExplanation: this.props.TemporaryAddressPurposeExplanation,
      ...queue,
    });
  };

  updateReferenceName = (values: HistoryResidenceItemModel['ReferenceName']) => {
    this.update({
      ReferenceName: values,
    });
  };

  updateReferenceName2013 = (values: HistoryResidenceItemModel['ReferenceName2013']) => {
    this.update({
      ReferenceName2013: values,
    });
  };

  updateReferenceLastContact = (values: HistoryResidenceItemModel['ReferenceLastContact']) => {
    this.update({
      ReferenceLastContact: values,
    });
  };

  updateReferenceRelationshipComments = (
    values: HistoryResidenceItemModel['ReferenceRelationshipComments']
  ) => {
    this.update({
      ReferenceRelationshipComments: values,
    });
  };

  updateReferenceRelationship = (values: CheckboxModel) => {
    const selected = [...((this.props.ReferenceRelationship || {}).values || [])];

    if (values.checked) {
      // Add the new relationship
      selected.push(values.value);
    } else if (selected.includes(values.value)) {
      // Remove the relationship if it was previously selected
      selected.splice(selected.indexOf(values.value), 1);
    }

    const explanationValue = (selected.includes('Other')) ? this.props.ReferenceRelationshipOther : {};

    this.update({
      ReferenceRelationship: {
        values: selected,
      },
      ReferenceRelationshipOther: explanationValue,
    });
  };

  updateReferenceRelationshipOther = (
    values: HistoryResidenceItemModel['ReferenceRelationshipOther']
  ) => {
    this.update({
      ReferenceRelationshipOther: values,
    });
  };

  updateReferencePhoneEvening = (values: HistoryResidenceItemModel['ReferencePhoneEvening']) => {
    this.update({
      ReferencePhoneEvening: values,
    });
  };

  updateReferencePhoneDay = (values: HistoryResidenceItemModel['ReferencePhoneDay']) => {
    this.update({
      ReferencePhoneDay: values,
    });
  };

  updateReferencePhoneMobile = (values: HistoryResidenceItemModel['ReferencePhoneMobile']) => {
    this.update({
      ReferencePhoneMobile: values,
    });
  };

  updateReferenceEmailNotApplicable = (
    values: HistoryResidenceItemModel['ReferenceEmailNotApplicable']
  ) => {
    this.update({
      ReferenceEmailNotApplicable: values,
      ReferenceEmail: values.applicable ? this.props.ReferenceEmail : {},
    });
  };

  updateReferenceEmail = (values: HistoryResidenceItemModel['ReferenceEmail']) => {
    this.update({
      ReferenceEmail: values,
      ReferenceEmailNotApplicable: { applicable: true },
    });
  };

  updateReferenceAddress = (values: HistoryResidenceItemModel['ReferenceAddress']) => {
    this.update({
      ReferenceAddress: values,
    });
  };

  updateAddress = (values: HistoryResidenceItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateDates = (values: HistoryResidenceItemModel['Dates']) => {
    const dates = this.props.Dates || {};
    const { from, to } = dates;
    // Only clear out the reference if the dates are outside 3 years. Unchecking present does not delete unless "to" is valid
    const zeroReference = !withinThreeYears(from, to, dates.present) && isValid(from) && (isValid(to) || dates.present);
    this.update({
      Dates: values,
      ReferenceName: zeroReference ? {} : this.props.ReferenceName,
      ReferenceName2013: zeroReference ? {} : this.props.ReferenceName2013,
      ReferenceLastContact: zeroReference ? {} : this.props.ReferenceLastContact,
      ReferenceRelationshipComments: zeroReference ? {} : this.props.ReferenceRelationshipComments,
      ReferenceRelationship: zeroReference ? {} : this.props.ReferenceRelationship,
      ReferenceRelationshipOther: zeroReference ? {} : this.props.ReferenceRelationshipOther,
      ReferencePhoneEvening: zeroReference ? {} : this.props.ReferencePhoneEvening,
      ReferencePhoneDay: zeroReference ? {} : this.props.ReferencePhoneDay,
      ReferencePhoneMobile: zeroReference ? {} : this.props.ReferencePhoneMobile,
      ReferenceEmailNotApplicable: zeroReference ? { applicable: true } : this.props.ReferenceEmailNotApplicable,
      ReferenceEmail: zeroReference ? {} : this.props.ReferenceEmail,
      ReferenceAddress: zeroReference ? {} : this.props.ReferenceAddress,
    });
  };

  updateRole = (values: HistoryResidenceItemModel['Role']) => {
    const isOther = values.value === 'Other';
    this.update({
      Role: values,
      RoleOther: isOther ? this.props.RoleOther : {}
    });
  };

  updateRoleOther = (values: HistoryResidenceItemModel['RoleOther']) => {
    this.update({
      RoleOther: values,
    });
  };

  updateComments = (values: HistoryResidenceItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  updateAddressPhysicalComments = (values: HistoryResidenceItemModel['AddressPhysicalComments']) => {
    this.update({
      AddressPhysicalComments: values,
    });
  };

  updateAddressAnswerComments = (values: HistoryResidenceItemModel['AddressAnswerComments']) => {
    this.update({
      AddressAnswerComments: values,
    });
  };

  updateAddressComments = (values: HistoryResidenceItemModel['AddressComments']) => {
    this.update({
      AddressComments: values,
    });
  };

  updateReferenceComments = (values: HistoryResidenceItemModel['ReferenceComments']) => {
    this.update({
      ReferenceComments: values,
    });
  };

  updateReferenceAlternateAddressPhysicalComments = (values: HistoryResidenceItemModel['ReferenceAlternateAddressPhysicalComments']) => {
    this.update({
      ReferenceAlternateAddressPhysicalComments: values,
    });
  };

  updateReferenceAlternateAddressAnswerComments = (values: HistoryResidenceItemModel['ReferenceAlternateAddressAnswerComments']) => {
    this.update({
      ReferenceAlternateAddressAnswerComments: values,
    });
  };

  updateReferenceAlternateAddressComments = (values: HistoryResidenceItemModel['ReferenceAlternateAddressComments']) => {
    this.update({
      ReferenceAlternateAddressComments: values,
    });
  };

  updateIsTemporaryAddress = (values: HistoryResidenceItemModel['IsTemporaryAddress']) => {
    const additionalUpdates = values?.value && values.value !== 'Yes' ? {
      TemporaryAddressPurpose: { value: '' },
      TemporaryAddressPurposeExplanation: {},
    } : {};

    this.update({
      IsTemporaryAddress: values,
      ...additionalUpdates,
    });
  };

  updateTemporaryAddressPurpose = (values: HistoryResidenceItemModel['TemporaryAddressPurpose']) => {
    this.update({
      TemporaryAddressPurpose: values,
    });
  };

  updateTemporaryAddressPurposeExplanation = (values: HistoryResidenceItemModel['TemporaryAddressPurposeExplanation']) => {
    this.update({
      TemporaryAddressPurposeExplanation: values,
    });
  };

  render() {
    // Certain elements are present if the date range of the residency was
    // within the last 3 years.
    const dates = this.props.Dates || {};
    const { requireHistoryResidenceReferenceInfo, requireHistoryResidenceRole } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);
    const addressLabel = this.props.$index === 0 ? i18n.t('history.pvq.residence.label.address') : i18n.t('history.pvq.residence.label.subsequent');

    return isPVQ ? (
      <div className="residence">
        <Field
          attribute={`${attribute}.Address`}
          optional
          onError={this.props.onError}
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocation
            attribute={`${attribute}.Address`}
            name="Address"
            {...this.props.Address}
            label={addressLabel}
            layout={Location.ADDRESS}
            geocode
            addressBook="Residence"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showMilitaryInstallation
            domesticTitle={i18n.t(`history.pvq.residence.address.domesticTitle`)}
            foreignTitle={i18n.t(`history.pvq.residence.address.foreignTitle`)}
            hideInstallationNameUnknownWhenForeign
            showApoFpoDpoPostcodeWhenForeign
        
            
            

          />
        </Field>

        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('history.pvq.residence.heading.dates')}
          titleSize="h4"
          help="history.residence.help.dates"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            label={i18n.t('history.pvq.residence.label.dates')}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            fromDateLabel={i18n.t('history.pvq.residence.date.fromDate')}
            toDateLabel={i18n.t('history.pvq.residence.date.toDate')}
            usePvqFromDate
            usePvqToDate
          />
        </Field>
        <Branch
          attribute={`${attribute}.IsTemporaryAddress`}
          name="is_temp_address"
          label={i18n.t('history.pvq.residence.label.temporaryAddress')}
          labelSize="h4"
          {...this.props.IsTemporaryAddress}
          onUpdate={this.updateIsTemporaryAddress}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.IsTemporaryAddress?.value === 'Yes' && (
          <>
            <Dropdown
              attribute={`${attribute}.TemporaryAddressPurpose`}
              name={this.props.name}
              label={i18n.t('history.pvq.residence.label.temporaryAddressPurpose')}
              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateTemporaryAddressPurpose}
              value={this.props.TemporaryAddressPurpose.value}
              required={this.props.required}
            >
              {temporaryLivingSituationOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
            {this.props.TemporaryAddressPurpose?.value === 'Other' && (
              <Field
                attribute={`${attribute}.TemporaryAddressPurposeExplanation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  label={i18n.t('history.pvq.residence.label.temporaryAddressPurposeExplanation')}
                  name="TemporaryAddressPurposeExplanation"
                  attribute={`${attribute}.TemporaryAddressPurposeExplanation`}
                  {...this.props.TemporaryAddressPurposeExplanation}
                  onUpdate={this.updateTemporaryAddressPurposeExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            )}
          </>
        )}
      </div>
    ) : (
      <div className="residence">
        <Field
          attribute={`${attribute}.Address`}
          title={i18n.t('history.residence.heading.address')}
          titleSize="h4"
          optional
          help="history.residence.help.address"
          onError={this.props.onError}
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <Location
            attribute={`${attribute}.Address`}
            name="Address"
            {...this.props.Address}
            label={i18n.t('history.residence.label.address')}
            layout={Location.ADDRESS}
            geocode
            addressBook="Residence"
            addressBooks={this.props.addressBooks}
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
          />
        </Field>

        <ConnectedAlternateAddress
          attribute={`${attribute}.AlternateAddress`}
          containingSection={HISTORY_RESIDENCE}
          address={this.props.AlternateAddress}
          belongingTo="AlternateAddress"
          country={this.props.Address?.country}
          militaryAddressLabel={i18n.t('address.militaryAddress.meResidence')}
          onUpdate={this.update}
          PhysicalLocationComments={this.props.AddressPhysicalComments}
          AddressComments={this.props.AddressComments}
          AnswerComments={this.props.AddressAnswerComments}
          updatePhysicalLocationComments={this.updateAddressPhysicalComments}
          updateAddressComments={this.updateAddressComments}
          updateAnswerComments={this.updateAddressAnswerComments}
        />

        <Field
          attribute={`${attribute}.Dates`}
          title={i18n.t('history.residence.heading.dates')}
          titleSize="h4"
          help="history.residence.help.dates"
          scrollIntoView={this.props.scrollIntoView}
        >
          {/* eslint jsx-a11y/label-has-associated-control: 0 */}
          {/* eslint jsx-a11y/label-has-for: 0 */}
          <label className="info-label">{i18n.t('history.residence.label.dates')}</label>

          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            label={i18n.t('history.residence.label.dates')}
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Show when={requireHistoryResidenceRole}>
          <Field
            attribute={`${attribute}.Role`}
            title={i18n.t('history.residence.heading.role')}
            titleSize="h4"
            className={classnames({
              'no-margin-bottom': this.props.Role?.value === 'Other',
            })}
            scrollIntoView={this.props.scrollIntoView}
          >
            <RadioGroup
              attribute={`${attribute}.Role`}
              className="role option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={this.props.Role?.value}
            >
              <Radio
                name="role-owned"
                label={i18n.t('history.residence.label.role.owned')}
                value="Own"
                onUpdate={this.updateRole}
                onError={this.props.onError}
              />
              <Radio
                name="role-rented"
                label={i18n.t('history.residence.label.role.rented')}
                value="Rent"
                onUpdate={this.updateRole}
                onError={this.props.onError}
              />
              <Radio
                name="role-military"
                label={i18n.t('history.residence.label.role.military')}
                value="MilitaryHousing"
                onUpdate={this.updateRole}
                onError={this.props.onError}
              />
              <Radio
                name="role-other"
                label={i18n.t('history.residence.label.role.other')}
                value="Other"
                onUpdate={this.updateRole}
                onError={this.props.onError}
              />
            </RadioGroup>
          </Field>

          <Show when={this.props.Role?.value === 'Other'}>
            <Field
              attribute={`${attribute}.RoleOther`}
              title={i18n.t('history.residence.label.role.explanation')}
              titleSize="h4"
              adjustFor="text"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Text
                attribute={`${attribute}.RoleOther`}
                name="RoleOther"
                {...this.props.RoleOther}
                className="other"
                maxlength="100"
                onUpdate={this.updateRoleOther}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        </Show>
        <Comments
          title={i18n.t('history.residence.label.comments')}
          name="Comments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        {(withinThreeYears(dates.from, dates.to, dates.present) === true)
          && (
            <div>
              <Field
                title={i18n.t('history.residence.heading.reference')}
                titleSize="h3"
                optional
                className="no-margin-bottom"
                scrollIntoView={this.props.scrollIntoView}
              >
                {i18n.t('history.residence.para.reference')}
              </Field>

              <div className="reference">
                <Field
                  title={i18n.t('reference.heading.name')}
                  titleSize="h4"
                  optional
                  filterErrors={Name.requiredErrorsOnly}
                  scrollIntoView={this.props.scrollIntoView}
                >
                  {this.props.requireHistoryResidenceReferenceName ? (
                    <Name
                      attribute={`${attribute}.ReferenceName`}
                      name="ReferenceName"
                      aria-label="Add a person that knows you"
                      prefix="name"
                      className="reference-name"
                      {...this.props.ReferenceName}
                      scrollIntoView={this.props.scrollIntoView}
                      onUpdate={this.updateReferenceName}
                      onError={this.handleNameError}
                      required={this.props.required}
                    />
                  ) : (
                    <Text
                      attribute={`${attribute}.ReferenceName2013`}
                      name="ReferenceName"
                      prefix="name"
                      className="reference-name2013"
                      {...this.props.ReferenceName2013}
                      scrollIntoView={this.props.scrollIntoView}
                      onUpdate={this.updateReferenceName2013}
                      onError={this.handleNameError}
                      required={this.props.required}
                    />
                  )}
                </Field>

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferenceLastContact`}
                    title={i18n.t('reference.heading.contact')}
                    titleSize="h4"
                    help="reference.help.contact"
                    adjustFor="labels"
                    shrink
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <DateControl
                      attribute={`${attribute}.ReferenceLastContact`}
                      name="ReferenceLastContact"
                      className="reference-last-contact"
                      {...this.props.ReferenceLastContact}
                      minDateEqualTo
                      onUpdate={this.updateReferenceLastContact}
                      onError={this.props.onError}
                      required={this.props.required}
                      hideDay
                    />
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferenceRelationship`}
                    title={i18n.t('reference.heading.relationship')}
                    titleSize="h4"
                    adjustFor="labels"
                    shrink
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <label>{i18n.t('reference.label.relationship.title')}</label>

                    <CheckboxGroup
                      attribute={`${attribute}.ReferenceRelationship`}
                      className="relationship option-list eapp-extend-labels option-list-vertical"
                      required={this.props.required}
                      onError={this.props.onError}
                      selectedValues={this.props.ReferenceRelationship?.values || []}
                    >
                      <Checkbox
                        name="relationship-neighbor"
                        className="reference-relationship-neighbor"
                        label={i18n.t('reference.label.relationship.neighbor')}
                        value="Neighbor"
                        onUpdate={this.updateReferenceRelationship}
                      />
                      <Checkbox
                        name="relationship-friend"
                        className="reference-relationship-friend"
                        label={i18n.t('reference.label.relationship.friend')}
                        value="Friend"
                        onUpdate={this.updateReferenceRelationship}
                      />
                      <Checkbox
                        name="relationship-landlord"
                        className="reference-relationship-landlord"
                        label={i18n.t('reference.label.relationship.landlord')}
                        value="Landlord"
                        onUpdate={this.updateReferenceRelationship}
                      />
                      <Checkbox
                        name="relationship-business"
                        className="reference-relationship-business"
                        label={i18n.t('reference.label.relationship.business')}
                        value="Business"
                        onUpdate={this.updateReferenceRelationship}
                      />
                      <Checkbox
                        name="relationship-other"
                        className="reference-relationship-other"
                        label={i18n.t('reference.label.relationship.other')}
                        value="Other"
                        onUpdate={this.updateReferenceRelationship}
                      />
                    </CheckboxGroup>
                    <Show
                      when={(this.props.ReferenceRelationship?.values || []).some(
                        (x) => x === 'Other'
                      )}
                    >
                      <Text
                        attribute={`${attribute}.ReferenceRelationshipOther`}
                        name="ReferenceRelationshipOther"
                        label={i18n.t('reference.label.relationship.explanation')}
                        maxlength="100"
                        className="relationship-other"
                        {...this.props.ReferenceRelationshipOther}
                        onUpdate={this.updateReferenceRelationshipOther}
                        onError={this.props.onError}
                        required={this.props.required}
                      />
                    </Show>
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    title={i18n.t('reference.heading.correspondence')}
                    titleSize="h3"
                    optional
                    className="no-margin-bottom"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    {i18n.t('reference.para.correspondence')}
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferencePhoneEvening`}
                    title={i18n.t('reference.heading.phone.evening')}
                    titleSize="h4"
                    className="override-required"
                    help="reference.help.phone"
                    adjustFor="telephone"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <Telephone
                      attribute={`${attribute}.ReferencePhoneEvening`}
                      name="ReferencePhoneEvening"
                      className="reference-phone-evening"
                      {...this.props.ReferencePhoneEvening}
                      onUpdate={this.updateReferencePhoneEvening}
                      onError={this.props.onError}
                      required={this.props.required}
                      showTimeOfDay={false}
                    />
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferencePhoneDay`}
                    title={i18n.t('reference.heading.phone.day')}
                    titleSize="h4"
                    className="override-required"
                    help="reference.help.phone"
                    adjustFor="telephone"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <Telephone
                      attribute={`${attribute}.ReferencePhoneDay`}
                      name="ReferencePhoneDay"
                      className="reference-phone-day"
                      {...this.props.ReferencePhoneDay}
                      onUpdate={this.updateReferencePhoneDay}
                      onError={this.props.onError}
                      required={this.props.required}
                      showTimeOfDay={false}
                    />
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferencePhoneMobile`}
                    title={i18n.t('reference.heading.phone.mobile')}
                    titleSize="h4"
                    className="override-required"
                    help="reference.help.phone"
                    adjustFor="telephone"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <Telephone
                      attribute={`${attribute}.ReferencePhoneMobile`}
                      name="ReferencePhoneMobile"
                      className="reference-phone-mobile"
                      {...this.props.ReferencePhoneMobile}
                      onUpdate={this.updateReferencePhoneMobile}
                      onError={this.props.onError}
                      required={this.props.required}
                      showTimeOfDay={false}
                    />
                  </Field>
                )}

                {requireHistoryResidenceReferenceInfo && (
                  <Field
                    attribute={`${attribute}.ReferenceEmail`}
                    title={i18n.t('reference.heading.email')}
                    titleSize="h4"
                    help="reference.help.email"
                    adjustFor="label"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <NotApplicable
                      attribute={`${attribute}.ReferenceEmailNotApplicable`}
                      name="ReferenceEmailNotApplicable"
                      {...this.props.ReferenceEmailNotApplicable}
                      label={i18n.t('reference.label.idk')}
                      or={i18n.t('reference.para.or')}
                      onUpdate={this.updateReferenceEmailNotApplicable}
                      onError={this.props.onError}
                    >
                      <Email
                        attribute={`${attribute}.ReferenceEmail`}
                        name="ReferenceEmail"
                        {...this.props.ReferenceEmail}
                        className="reference-email"
                        label={i18n.t('reference.label.email')}
                        onUpdate={this.updateReferenceEmail}
                        onError={this.props.onError}
                      />
                    </NotApplicable>
                  </Field>
                )}

                <Field
                  attribute={`${attribute}.ReferenceAddress`}
                  title={i18n.t('reference.heading.address')}
                  titleSize="h4"
                  optional
                  help="reference.help.address"
                  adjustFor="address"
                  scrollIntoView={this.props.scrollIntoView}
                >
                  <p>{i18n.t('reference.para.address')}</p>

                  <Location
                    attribute={`${attribute}.ReferenceAddress`}
                    name="ReferenceAddress"
                    className="reference-address"
                    {...this.props.ReferenceAddress}
                    label={i18n.t('reference.label.address')}
                    layout={Location.ADDRESS}
                    geocode
                    addressBooks={this.props.addressBooks}
                    addressBook="Reference"
                    showPostOffice
                    dispatch={this.props.dispatch}
                    onUpdate={this.updateReferenceAddress}
                    onError={this.props.onError}
                  />
                </Field>
                <Comments
                  title={i18n.t('history.residence.label.referencecomments')}
                  name="Reference Comments"
                  className="optional-comments"
                  attribute="ReferenceComments"
                  {...this.props.ReferenceComments}
                  onUpdate={this.updateReferenceComments}
                  required={false}
                />

                {requireHistoryResidenceReferenceInfo && (
                  <ConnectedAlternateAddress
                    attribute={`${attribute}.ReferenceAlternateAddress`}
                    containingSection={HISTORY_RESIDENCE}
                    belongingTo="ReferenceAlternateAddress"
                    address={this.props.ReferenceAlternateAddress}
                    country={this.props.ReferenceAddress?.country}
                    militaryAddressLabel={i18n.t('address.militaryAddress.residenceVerifier')}
                    onUpdate={this.update}
                    PhysicalLocationComments={this.props.ReferenceAlternateAddressPhysicalComments}
                    AddressComments={this.props.ReferenceAlternateAddressComments}
                    AnswerComments={this.props.ReferenceAlternateAddressAnswerComments}
                    updatePhysicalLocationComments={this.updateReferenceAlternateAddressPhysicalComments}
                    updateAddressComments={this.updateReferenceAlternateAddressComments}
                    updateAnswerComments={this.updateReferenceAlternateAddressAnswerComments}
                  />
                )}
              </div>
            </div>
          )}
      </div>
    );
  }
}

ResidenceItem.defaultProps = {
  Dates: {},
  Address: {},
  Comments: {},
  AddressPhysicalComments: {},
  AddressComments: {},
  AddressAnswerComments: {},
  IsTemporaryAddress: {},
  ReferenceAlternateAddressPhysicalComments: {},
  ReferenceAlternateAddressAnswerComments: {},
  ReferenceAlternateAddressComments: {},
  ReferenceComments: {},
  Role: {},
  RoleOther: {},
  ReferenceName: {},
  ReferenceLastContact: {},
  ReferenceRelationshipComments: {},
  ReferenceRelationship: {},
  ReferenceRelationshipOther: {},
  ReferencePhoneEvening: {},
  ReferencePhoneDay: {},
  ReferencePhoneMobile: {},
  ReferenceEmailNotApplicable: { applicable: true },
  ReferenceEmail: {},
  ReferenceAddress: {},
  TemporaryAddressPurpose: { value: '' },
  TemporaryAddressPurposeExplanation: {},
  addressBooks: {},
  requireHistoryResidenceRole: true,
  requireHistoryResidenceReferenceInfo: true,
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
